import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export function MerchOrderCard({ o }) {
  const t = (o.shipments || []).slice(-1)[0];
  return (
    <div className="rounded-2xl border p-4 flex gap-4" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`merch-order-${o.id}`}>
      {o.image_url && <img src={o.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-serif text-base" style={{ color: "var(--burgundy)" }}>{o.product_title}</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{o.variant_title} · Qty {o.quantity} · {usd(o.amount_cents)} incl. shipping · {(o.paid_at || o.created_at || "").slice(0, 10)}</div>
        <div className="mt-1 text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--teal-deep)" }} data-testid={`merch-order-${o.id}-status`}>{o.payment_status === "refunded" ? "Refunded" : o.fulfillment_label}</div>
        {t?.number && <div className="text-xs mt-1">Tracking: {t.url ? <a href={t.url} target="_blank" rel="noopener noreferrer" className="ink-link" style={{ color: "var(--burgundy)" }}>{t.carrier} {t.number}</a> : `${t.carrier || ""} ${t.number}`}</div>}
      </div>
    </div>
  );
}

export default function MerchThankYou() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const { user } = useApp();
  const [st, setSt] = useState({ state: "polling" });
  const n = useRef(0);
  useEffect(() => {
    if (!sid) { setSt({ state: "error" }); return; }
    let t;
    const poll = async () => {
      n.current += 1;
      try {
        const { data } = await api.get(`/store/merch/status/${sid}`);
        if (data.payment_status === "paid") { setSt({ state: "done", o: data }); return; }
        if (data.payment_status === "expired") { setSt({ state: "failed" }); return; }
        if (n.current >= 12) { setSt({ state: "timeout" }); return; }
        t = setTimeout(poll, 2000);
      } catch { setSt({ state: "error" }); }
    };
    poll(); return () => clearTimeout(t);
  }, [sid]);
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid={`merch-thankyou-${st.state}`}>
      <div className="rounded-3xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        {st.state === "polling" && <><Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--burgundy)" }} /><p className="text-sm" style={{ color: "var(--text-muted)" }}>Confirming your order with Stripe…</p></>}
        {st.state === "done" && <>
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--burgundy)" }} />
          <div className="eyebrow mb-2">Order received</div>
          <h1 className="font-serif text-3xl mb-4" style={{ color: "var(--burgundy)" }}>Thank you for your order.</h1>
          <div className="text-left"><MerchOrderCard o={st.o} /></div>
          <p className="text-sm mt-5" style={{ color: "var(--text-obsidian)" }}>Shipping to {st.o.recipient?.first_name} {st.o.recipient?.last_name}, {st.o.recipient?.city}. A confirmation has been sent by email; we will write again when it ships.</p>
          <div className="mt-6 flex justify-center gap-3">{user && <Link to="/my-temple" className="rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} data-testid="merch-thankyou-mytemple-btn">My Temple</Link>}<Link to="/store" className="rounded-full px-5 py-2.5 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Back to Store</Link></div>
        </>}
        {["failed", "timeout", "error"].includes(st.state) && <><AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--rose-dark)" }} /><h2 className="font-serif text-2xl mb-3" style={{ color: "var(--burgundy)" }}>{st.state === "failed" ? "Checkout was not completed." : "We couldn't confirm this yet."}</h2><p className="text-sm" style={{ color: "var(--text-muted)" }}>If your payment completed, your order will be recorded once Stripe confirms it. You have not been charged twice.</p></>}
      </div>
    </div>
  );
}
